import React, { useState, useEffect, useRef } from 'react';
import { ColorFilter, FilterState, Season } from '../../types/ColorLibraryModel';

interface ColorFiltersProps {
  filters: ColorFilter[];
  colors: any[];
  onFilterChange: (filters: FilterState | null) => void;
  onSelectAllColors: () => void;
}

const ColorFilters: React.FC<ColorFiltersProps> = ({ filters, colors, onFilterChange, onSelectAllColors }) => {
  const [filterStates, setFilterStates] = useState<{ [key: string]: string[] }>({});
  const [openDropdowns, setOpenDropdowns] = useState<{ [key: number]: boolean }>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [windowWidth, setWindowWidth] = useState(1024);
  const dropdownRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  useEffect(() => {
    const initialStates: { [key: string]: string[] } = {};
    filters.forEach((filter) => {
      initialStates[filter.label] = [];
    });
    setFilterStates(initialStates);
    setOpenDropdowns({});
    setIsInitialized(true);
  }, [filters]);

  useEffect(() => {
    if (Object.keys(filterStates).length > 0) {
      const mappedFilters: any = {};
      filters.forEach((filter, index) => {
        if (index === 0) mappedFilters.colourGroupFilter = filterStates[filter.label] || [];
        else if (index === 1) mappedFilters.effectFilter = filterStates[filter.label] || [];
        else if (index === 2) mappedFilters.seasonFilter = filterStates[filter.label] || [];
      });
      // Use requestAnimationFrame to prevent flashing
      requestAnimationFrame(() => onFilterChange(mappedFilters));
    }
  }, [filterStates, onFilterChange, filters]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.keys(dropdownRefs.current).forEach((key) => {
        const ref = dropdownRefs.current[Number(key)];
        if (ref && !ref.contains(event.target as Node)) {
          setOpenDropdowns((prev) => ({ ...prev, [key]: false }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resetFilters = () => {
    const resetStates: { [key: string]: string[] } = {};
    filters.forEach((filter) => {
      resetStates[filter.label] = [];
    });
    setFilterStates(resetStates);
    onFilterChange(null);
  };

  const noFilterSelected = colors.length === 0 && Object.values(filterStates).every((arr) => arr.length === 0);

  const handleFilterChange = (filterLabel: string, value: string) => {
    setFilterStates((prev) => {
      const current = prev[filterLabel] || [];
      const newFilter = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
      return { ...prev, [filterLabel]: newFilter };
    });
  };

  const getDisplayText = (selected: string[], filterType: string, count: number) => {
    if (selected.length === 0) {
      return `${filterType} (${count})`;
    } else if (selected.length === 1) {
      return selected[0];
    } else {
      return `${selected[0]} (+${selected.length - 1})`;
    }
  };

  const renderFilterOptions = (filter: ColorFilter, index: number) => {
    const currentValues = filterStates[filter.label] || [];
    const isSeasonFilter = filter.types.length > 0 && typeof filter.types[0] === 'object';
    const isMobile = windowWidth < 768;

    const displayValues = isSeasonFilter
      ? currentValues.map((code) => {
          const season = (filter.types as Season[]).find((s) => s.categoryCodeName === code);
          return season?.categoryDisplayName || code;
        })
      : currentValues;

    return (
      <div
        key={index}
        className="relative"
        ref={(el) => {
          dropdownRefs.current[index] = el;
        }}
      >
        <button
          onClick={() => setOpenDropdowns((prev) => ({ ...prev, [index]: !prev[index] }))}
          className={`flex cursor-pointer appearance-none items-center justify-between rounded-full border border-gray-300 bg-white text-left transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:outline-none ${
            isMobile ? 'h-11 w-[180px] px-3 py-2 text-sm' : 'h-11 w-[180px] px-4 py-2 text-sm lg:text-base'
          }`}
        >
          <span className="truncate pr-2">
            {getDisplayText(displayValues, filter.label, filter.types?.length || 0)}
          </span>
          <div className="flex items-center gap-1">
            {currentValues.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setFilterStates((prev) => ({ ...prev, [filter.label]: [] }));
                }}
                className="mb-[10px] h-4 w-4 flex-shrink-0 cursor-pointer text-gray-400 transition-colors hover:text-gray-600"
              >
                ✕
              </button>
            )}
            <svg
              className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-200 ${openDropdowns[index] ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        {openDropdowns[index] && (
          <div
            className={`animate-in fade-in-0 zoom-in-95 absolute z-50 mt-1 max-h-60 overflow-auto rounded-md border border-gray-300 bg-white shadow-lg duration-200 ${
              isMobile ? 'w-full' : 'w-full min-w-[200px]'
            }`}
            data-lenis-prevent
          >
            {filter.types?.map((type, typeIndex) => {
              const isSeasonType = typeof type === 'object';
              const value = isSeasonType ? (type as Season).categoryCodeName : (type as string);
              const label = isSeasonType ? (type as Season).categoryDisplayName : (type as string);

              return (
                <label
                  key={typeIndex}
                  className="flex cursor-pointer items-center px-3 py-2 transition-colors hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={currentValues.includes(value)}
                    onChange={() => handleFilterChange(filter.label, value)}
                    className="mr-3 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-900">{label}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (!isInitialized || filters.length === 0) {
    return null;
  }

  const isMobile = windowWidth < 768;

  return (
    <div
      className={`flex flex-wrap items-center gap-2 sm:gap-3 ${
        isMobile ? 'justify-center px-2' : 'justify-center sm:justify-start'
      }`}
    >
      {filters.map((filter, index) => renderFilterOptions(filter, index))}

      <div className="flex items-center gap-2 sm:gap-3">
        <button
          onClick={onSelectAllColors}
          className="group relative cursor-pointer overflow-hidden px-2 py-2 text-xs font-semibold text-gray-700 transition-colors duration-200 hover:text-gray-900 focus:outline-none sm:px-3 sm:text-sm lg:px-4 lg:text-base"
        >
          <span className="relative z-10">select all</span>
          <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-black transition-all duration-300 group-hover:w-full" />
        </button>

        <button
          onClick={resetFilters}
          disabled={noFilterSelected}
          className={`group relative overflow-hidden px-2 py-2 text-xs font-semibold transition-colors duration-200 focus:outline-none sm:px-3 sm:text-sm lg:px-4 lg:text-base ${
            noFilterSelected ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-gray-700 hover:text-gray-900'
          }`}
        >
          <span className="relative z-10">clear all</span>
          <div
            className={`absolute bottom-0 left-0 h-0.5 w-0 bg-black transition-all duration-300 ${
              !noFilterSelected ? 'group-hover:w-full' : ''
            }`}
          />
        </button>
      </div>
    </div>
  );
};

export default ColorFilters;
